import React, { useMemo } from 'react';
import { HotelPlusAirports, HotelPlusDurations, Maybe, Sale } from '@gql/schema';
import { ITranslationsObject, i18n } from '@secretescapes/i18n';
import { FlightsSelectOption, IOffer, ISelectOption } from './Flights.interfaces';

interface IUseFlightStateArgs {
  flightsEnabled: boolean;
  translations: ITranslationsObject;
  sale: Maybe<Sale>;
  bypassInitialDuration?: boolean;
  tripTypeParam?: Maybe<string>;
  durationParam?: Maybe<string>;
  airportCodeParam?: Maybe<string>;
  airportNameParam?: Maybe<string>;
}

export type TWithFlightsOffers = Record<string, { price: string; packageRef: string }> | undefined;
export type TWithFlightsDescription = (description: string) => string;

interface IUseFlightStateResult {
  flightState: IFlightState;
  setFlightState: React.Dispatch<React.SetStateAction<IFlightState>>;
  withFlightsOffers: TWithFlightsOffers;
  withFlightsDescription: TWithFlightsDescription;
  flightsSelected: boolean;
  setFlightsSelected: (arg: boolean) => void;
}

export interface IFlightOffer {
  externalReference: string;
  offerId: string;
  priceForDisplay: string;
}

export interface IFlightState {
  airports: ISelectOption[];
  nights: ISelectOption[];
  offers: IFlightOffer[] | undefined;
  selectedAirport: ISelectOption;
  selectedNights: ISelectOption;
}

export const getAirportOptions = (
  data: Maybe<HotelPlusAirports[]>,
  translations: ITranslationsObject
): ISelectOption[] => {
  return (data ?? []).map(entry => ({
    id: entry.departureAirportCode,
    title: entry.departureAirportName ?? '',
    price: `${i18n('common.fromPrice.label', [], translations)} ${entry.priceForDisplay}`,
  }));
};

export const getNightOptions = (
  data: Maybe<HotelPlusDurations[]>,
  translations: ITranslationsObject
): ISelectOption[] => {
  return (data ?? []).map(entry => ({
    id: entry?.duration,
    title: entry?.duration
      ? i18n('common.nights.text', [entry.duration], translations)
      : i18n('flights.all.durations', [], translations),
    price: `${i18n('common.fromPrice.label', [], translations)} ${entry.priceForDisplay}`,
  }));
};

/**
 * This will be used to store flight related gloabl state
 */
export const useFlightState: (args: IUseFlightStateArgs) => IUseFlightStateResult = ({
  flightsEnabled,
  sale,
  translations,
  bypassInitialDuration = false,
  tripTypeParam,
  durationParam,
  airportCodeParam,
  airportNameParam,
}) => {
  /**
   * Populate initial flight state in order of priority
   * - FROM url param values
   * - FROM sale prices withFlights
   */
  const { withFlights } = sale?.prices ?? {};
  const initialDepartureAirportName = airportNameParam ?? withFlights?.departureAirportName ?? '';
  const initialDepartureAirportCode = airportCodeParam ?? withFlights?.departureAirportCode ?? '';
  const initialDuration = bypassInitialDuration
    ? Number(durationParam) || null
    : withFlights?.duration;

  const initialFlightsSelected =
    flightsEnabled && tripTypeParam === FlightsSelectOption.hotelWithFlights;

  const [flightsSelected, setFlightsSelected] = React.useState(initialFlightsSelected);
  const [flightState, setFlightState] = React.useState<IFlightState>({
    airports: [],
    nights: [],
    offers: undefined,
    selectedAirport: {
      title: initialDepartureAirportName,
      id: initialDepartureAirportCode,
      price: '',
    },
    selectedNights: {
      title: initialDuration
        ? i18n('common.nights.text', [initialDuration], translations)
        : i18n('flights.all.durations', [], translations),
      id: initialDuration,
      price: '',
    },
  });

  const withFlightsOffers: TWithFlightsOffers = useMemo(
    () =>
      flightState?.offers?.reduce(
        (acc: Record<string, { price: string; packageRef: string }>, offer: IOffer) => ({
          ...acc,
          [offer.offerId]: {
            price: offer.priceForDisplay,
            packageRef: offer.externalReference,
          },
        }),
        {}
      ),
    [flightState.offers]
  );

  // If withFlights offers page then remove 'Travel is not included' from offer descriptions
  const withFlightsDescription: TWithFlightsDescription = description => {
    const travelNotIncludedText: string = i18n('offer.info.travel.not.included', [], translations);

    return description
      .split('</li>')
      .filter((el: string) => !el.match(travelNotIncludedText))
      .join('</li>');
  };

  return {
    flightState,
    setFlightState,
    withFlightsOffers,
    withFlightsDescription,
    flightsSelected,
    setFlightsSelected,
  };
};
